/**
 * Pure path helpers for the shell navigation (plan 08 WP-VIS-02).
 *
 * No React, no icons and no path aliases, so the sidebar, the mobile sheet,
 * the header and Node's test runner (`npm run test:unit`) share one rule.
 */

/**
 * Prefix match, so nested routes keep their nav item lit: `/workbench/123`
 * lights Workbench and `/admin/users/7` lights User Management. `/` matches
 * only itself, so the root entry never lights for every page.
 */
export function isNavItemActive(pathname: string | null | undefined, href: string): boolean {
  if (!pathname) return false
  if (href === '/') return pathname === '/'
  return pathname === href || pathname.startsWith(`${href}/`)
}

export interface Crumb {
  label: string
  href: string
}

/**
 * Labels for path segments that are not a nav entry of their own, or whose
 * capitalised form reads wrong ("Ai", "Agent hub"). Every other segment is
 * title-cased with hyphens turned into spaces.
 */
export const SEGMENT_LABELS: Readonly<Record<string, string>> = {
  ai: 'AI',
  'agent-hub': 'AI Coworker Hub',
  'ai-coworker-hub': 'AI Coworker Hub',
  'data-manager': 'Data Manager',
  'tax-hub': 'Tax Hub',
  contracts: 'Contract Hub',
}

export function segmentLabel(segment: string): string {
  return SEGMENT_LABELS[segment] ?? segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ')
}

/**
 * The header breadcrumb (plan 06 WP-UX-01): the root crumb first, then one
 * crumb per accumulated path. Each crumb takes the nav label for its exact
 * href when there is one (`labelFor`), so the breadcrumb and the sidebar say
 * the same thing, and falls back to `segmentLabel` otherwise.
 */
export function breadcrumbTrail(
  pathname: string | null | undefined,
  labelFor: (href: string) => string | undefined,
  rootLabel: string
): Crumb[] {
  const trail: Crumb[] = [{ label: labelFor('/') ?? rootLabel, href: '/' }]
  let current = ''
  for (const segment of (pathname ?? '/').split('/').filter(Boolean)) {
    current += `/${segment}`
    trail.push({ label: labelFor(current) ?? segmentLabel(segment), href: current })
  }
  return trail
}

// ─── Page-provided crumb labels ────────────────────────────────────────────
//
// A detail page whose path ends in a database id ("/review/1") names its own
// crumb ("Invoice NPC-24117") once it has loaded. The header subscribes, so
// the label appears without a navigation. Cleared when the page unmounts.

const crumbOverrides = new Map<string, string>()
const crumbListeners = new Set<() => void>()
let crumbVersion = 0

/** Set (or with `null` clear) the crumb label for one exact href. */
export function setCrumbLabel(href: string, label: string | null): void {
  const text = label?.trim() || null
  if ((crumbOverrides.get(href) ?? null) === text) return
  if (text) crumbOverrides.set(href, text)
  else crumbOverrides.delete(href)
  crumbVersion += 1
  crumbListeners.forEach((listener) => listener())
}

export function crumbLabelFor(href: string): string | undefined {
  return crumbOverrides.get(href)
}

export function subscribeCrumbLabels(listener: () => void): () => void {
  crumbListeners.add(listener)
  return () => {
    crumbListeners.delete(listener)
  }
}

export function crumbLabelsVersion(): number {
  return crumbVersion
}
